import { apiGet, apiPost, apiDelete } from './api'

// ==================== Types ====================

export interface AttendanceSummary {
  date: string
  shift?: {
    id: number
    name: string
    start_time: string
    end_time: string
  }
  check_in?: string
  check_out?: string
  working_hours?: number
  status: 'present' | 'absent' | 'late' | 'half_day' | 'on_leave'
  monthly_summary?: {
    total_working_days?: number
    total_attendance?: number
    total_absent?: number
    total_late?: number
    total_remaining_leave?: number
    [key: string]: number | undefined
  }
}

export interface AttendanceRecord {
  id: number
  date: string
  check_in?: string
  check_out?: string
  working_hours?: number
  status: string
  late_minutes?: number
  early_leave_minutes?: number
  overtime_minutes?: number
  location_in?: string
  location_out?: string
}

export interface AttendanceHistory {
  data: AttendanceRecord[]
  summary: {
    total_days: number
    present: number
    absent: number
    late: number
    leaves: number
    half_days: number
  }
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface LeaveType {
  id: number
  name: string
  description?: string
  max_days?: number
  requires_approval: boolean
}

export interface LeaveRequest {
  id: number
  leave_type: LeaveType
  start_date: string
  end_date: string
  days: number
  reason?: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  is_half_day: boolean
  half_day_type?: 'first_half' | 'second_half'
  approved_by?: string
  approved_at?: string
  rejected_reason?: string
  created_at: string
  leave_ids?: number[]
}

export interface LeaveBalance {
  annual: number
  sick: number
  personal: number
  [key: string]: number
}

export interface LeaveQuota {
  leave_type_id: number
  name: string
  color: string
  quota: number
  used: number
  pending: number
  remaining: number | null
}

export interface MyLeavesResponse {
  data: LeaveRequest[]
  balance?: LeaveBalance
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface CheckInOutResponse {
  success: boolean
  check_in_time?: string
  check_out_time?: string
  working_hours?: number
  message: string
}

export interface CreateLeaveData {
  leave_type_id: number
  start_date: string
  end_date: string
  reason?: string
  is_half_day?: boolean
  half_day_type?: 'first_half' | 'second_half'
}

export interface PendingLeaveEmployee {
  id: number
  name: string
  email: string
  department: string
  designation: string
}

export interface PendingLeaveRequest {
  id: number
  employee: PendingLeaveEmployee
  leave_type: { id: number; name: string }
  start_date: string
  end_date: string
  days: number
  reason?: string
  status: string
  is_half_day: boolean
  created_at: string
  leave_ids?: number[]
}

export interface PendingLeavesResponse {
  data: PendingLeaveRequest[]
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface SyncLog {
  id: number
  company_id: number
  sync_type: string
  status: 'pending' | 'success' | 'failed'
  records_synced: number
  records_created: number
  records_updated: number
  error_message?: string
  started_at: string
  completed_at?: string
  initiated_by: number
}

export interface SyncResult {
  success: boolean
  message: string
  created: number
  updated: number
  errors?: string[]
  sync_log_id: number
}

export interface LocalAttendanceToday {
  id: number
  user_id: number
  date: string
  check_in_time: string | null
  check_out_time: string | null
  working_hours: number | null
  status: string | null
}

// ==================== Attendance API ====================

export const getTodayAttendance = async (): Promise<LocalAttendanceToday | null> => {
  return apiGet<LocalAttendanceToday | null>('/eagle-vision/attendance/today')
}

export const getAttendanceSummary = async (date?: string): Promise<AttendanceSummary> => {
  const params = date ? { date } : undefined
  return apiGet<AttendanceSummary>('/eagle-vision/attendance/summary', params)
}

export const getAttendanceHistory = async (
  startDate: string,
  endDate: string,
  page = 1,
  perPage = 31
): Promise<AttendanceHistory> => {
  return apiGet<AttendanceHistory>('/eagle-vision/attendance/history', {
    start_date: startDate,
    end_date: endDate,
    page,
    per_page: perPage,
  })
}

export const checkIn = async (
  latitude?: number,
  longitude?: number,
  location?: string,
  image?: string
): Promise<CheckInOutResponse> => {
  return apiPost<CheckInOutResponse>('/eagle-vision/attendance/check-in', {
    latitude,
    longitude,
    location,
    image,
  })
}

export const checkOut = async (
  latitude?: number,
  longitude?: number,
  location?: string,
  image?: string
): Promise<CheckInOutResponse> => {
  return apiPost<CheckInOutResponse>('/eagle-vision/attendance/check-out', {
    latitude,
    longitude,
    location,
    image,
  })
}

// ==================== Leave API ====================

export const getLeaveQuota = async (): Promise<LeaveQuota[]> => {
  const response = await apiGet<{ balances: LeaveQuota[] }>('/eagle-vision/attendance/leave/quota')
  return response.balances
}

export const getLeaveTypes = async (): Promise<LeaveType[]> => {
  const response = await apiGet<{ leave_types: LeaveType[] }>('/eagle-vision/attendance/leave/types')
  return response.leave_types
}

export const getMyLeaves = async (
  status?: string,
  year?: number,
  page = 1,
  perPage = 20
): Promise<MyLeavesResponse> => {
  return apiGet<MyLeavesResponse>('/eagle-vision/attendance/leave/my', {
    status,
    year,
    page,
    per_page: perPage,
  })
}

export const createLeave = async (data: CreateLeaveData): Promise<{ success: boolean; leave_request: LeaveRequest; message: string }> => {
  return apiPost('/eagle-vision/attendance/leave', data)
}

export const cancelLeave = async (leaveId: number): Promise<{ success: boolean; message: string }> => {
  return apiDelete(`/eagle-vision/attendance/leave/${leaveId}`)
}

export const getPendingLeaves = async (
  status = 'pending',
  page = 1,
  perPage = 50
): Promise<PendingLeavesResponse> => {
  return apiGet<PendingLeavesResponse>('/eagle-vision/attendance/leave/pending', {
    status,
    page,
    per_page: perPage,
  })
}

interface LeaveActionContext {
  leaveIds?: number[]
  employee_id?: number
  leave_type_name?: string
  date_display?: string
}

export const approveLeave = async (leaveId: number, remarks?: string, ctx?: LeaveActionContext): Promise<{ success: boolean; message: string }> => {
  const payload = { remarks, employee_id: ctx?.employee_id, leave_type_name: ctx?.leave_type_name, date_display: ctx?.date_display }
  if (ctx?.leaveIds && ctx.leaveIds.length > 1) {
    return apiPost('/eagle-vision/attendance/leave/bulk-approve', { ...payload, leave_ids: ctx.leaveIds })
  }
  return apiPost(`/eagle-vision/attendance/leave/${leaveId}/approve`, payload)
}

export const rejectLeave = async (leaveId: number, reason?: string, ctx?: LeaveActionContext): Promise<{ success: boolean; message: string }> => {
  const payload = { reason, employee_id: ctx?.employee_id, leave_type_name: ctx?.leave_type_name, date_display: ctx?.date_display }
  if (ctx?.leaveIds && ctx.leaveIds.length > 1) {
    return apiPost('/eagle-vision/attendance/leave/bulk-reject', { ...payload, leave_ids: ctx.leaveIds })
  }
  return apiPost(`/eagle-vision/attendance/leave/${leaveId}/reject`, payload)
}

// ==================== Sync API (Admin only) ====================

export const syncEmployees = async (): Promise<SyncResult> => {
  return apiPost<SyncResult>('/eagle-vision/sync/employees')
}

export const syncSingleEmployee = async (employeeId: number): Promise<{ success: boolean; action: string; user: object }> => {
  return apiPost(`/eagle-vision/sync/employee/${employeeId}`)
}

export const getSyncStatus = async (): Promise<{ logs: SyncLog[]; last_sync: SyncLog | null }> => {
  return apiGet('/eagle-vision/sync/status')
}

export const getSyncLogs = async (
  page = 1,
  perPage = 20,
  syncType?: string
): Promise<{ logs: SyncLog[]; pagination: object }> => {
  return apiGet('/eagle-vision/sync/logs', {
    page,
    per_page: perPage,
    type: syncType,
  })
}

export const getEagleVisionDepartments = async (): Promise<{ departments: object[] }> => {
  return apiGet('/eagle-vision/sync/departments')
}

export const getEagleVisionDesignations = async (): Promise<{ designations: object[] }> => {
  return apiGet('/eagle-vision/sync/designations')
}

// ==================== Timelog Types ====================

export interface Timelog {
  id: number
  project_id?: number
  task_id: number
  user_id: number
  start_time: string
  end_time?: string
  memo?: string
  total_hours?: number
  total_minutes?: number
  total_break_minutes?: number
  project?: { id: number; name: string }
  task?: { id: number; name: string }
}

export interface ActiveTimer {
  id: number
  task_id: number
  memo?: string
  start_time: string
  project?: { id: number; name: string }
  task?: { id: number; name: string }
  breaks?: Array<{ id: number; start_time: string; end_time?: string }>
}

export interface ActiveBreak {
  id: number
  start_time: string
  reason?: string
}

// ==================== Timelog API ====================

export const getActiveTimer = async (): Promise<{ data?: ActiveTimer }> => {
  return apiGet('/eagle-vision/timelog/active-timer')
}

export const getActiveBreak = async (): Promise<{ data?: ActiveBreak }> => {
  return apiGet('/eagle-vision/timelog/active-break')
}

export const getMyTimelogs = async (
  startDate?: string,
  endDate?: string
): Promise<{ data: Timelog[] }> => {
  const params: Record<string, string> = {}
  if (startDate) params.start_date = startDate
  if (endDate) params.end_date = endDate
  return apiGet('/eagle-vision/timelog/me', params)
}

export const startTimer = async (
  taskId: string,
  memo: string,
  projectId?: string,
  latitude?: number,
  longitude?: number,
  image?: string
): Promise<{ data?: ActiveTimer; message?: string }> => {
  return apiPost('/eagle-vision/timelog/start-timer', {
    task_id: taskId,
    memo,
    project_id: projectId,
    latitude,
    longitude,
    image,
  })
}

export const pauseTimer = async (timeId: number): Promise<{ data?: ActiveBreak; message?: string }> => {
  return apiPost('/eagle-vision/timelog/pause-timer', { time_id: timeId })
}

export const resumeTimer = async (breakId: number): Promise<{ message?: string }> => {
  return apiPost('/eagle-vision/timelog/resume-timer', { break_id: breakId })
}

export const stopTimer = async (
  timeId: number,
  latitude?: number,
  longitude?: number,
  image?: string
): Promise<{ message?: string }> => {
  return apiPost('/eagle-vision/timelog/stop-timer', {
    time_id: timeId,
    latitude,
    longitude,
    image,
  })
}

// ==================== Learning API ====================

export interface KnowledgeAttachment {
  id?: number
  filename: string
  url?: string | null
  download_url?: string | null
  size?: number | null
  icon?: string | null
}

export interface KnowledgeArticle {
  id: number
  heading: string
  description?: string | null
  to?: string
  departments?: number[] | null
  category_id?: number | null
  category?: { id: number; name: string } | null
  attachments?: KnowledgeAttachment[] | null
  created_at?: string
}

export interface LearningQuestionnaire {
  id: number
  title: string
  exam_key?: string
  duration?: number
  start_date?: string | null
  end_date?: string | null
  [key: string]: unknown
}

export interface LearningQuestion {
  id: number
  question: string
  note?: string | null
  options: Array<{ id: number; option?: string; name?: string; title?: string; [key: string]: unknown }>
}

export interface LearningQuestionnaireResult {
  attempt_id?: number
  questionnaire_id?: number
  user_id?: number
  finished?: boolean
  completed_at?: string
  submitted_date?: string
  answered_questions?: number
  total_questions?: number
  total_correct_answer?: number
  total_false_answer?: number
  percentage?: number
  [key: string]: unknown
}

export interface FinishLearningQuestionnaireResponse {
  status?: string
  code?: number
  message?: string
  data?: {
    result?: LearningQuestionnaireResult
    [key: string]: unknown
  }
}

export interface EagleVisionPage<T> {
  status?: string
  code?: number
  message?: string
  data: T[]
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number }
}

export interface EagleVisionResource<T> {
  status?: string
  code?: number
  message?: string
  data: T
}

const learningBase = '/eagle-vision/learning'

export const getLearningUpdates = (options: { articlePage?: number; questionnairePage?: number; perPage?: number; search?: string } = {}) =>
  apiGet<{
    articles: EagleVisionPage<KnowledgeArticle>
    questionnaires: EagleVisionPage<LearningQuestionnaire>
    new_content: { articles: number; questionnaires: number }
  }>(`${learningBase}/updates`, {
    article_page: options.articlePage ?? 1,
    questionnaire_page: options.questionnairePage ?? 1,
    per_page: options.perPage ?? 10,
    ...(options.search?.trim() ? { search: options.search.trim() } : {}),
  })

export const getKnowledgeArticles = (page = 1, perPage = 20, search = '') =>
  apiGet<EagleVisionPage<KnowledgeArticle>>(`${learningBase}/articles`, {
    page,
    per_page: perPage,
    ...(search.trim() ? { search: search.trim() } : {}),
  })

export const getKnowledgeArticle = (id: number) =>
  apiGet<EagleVisionResource<KnowledgeArticle>>(`${learningBase}/articles/${id}`)

export const getLearningQuestionnaires = (page = 1, perPage = 20, search = '') =>
  apiGet<EagleVisionPage<LearningQuestionnaire>>(`${learningBase}/questionnaires`, {
    page,
    per_page: perPage,
    ...(search.trim() ? { search: search.trim() } : {}),
  })

export const getLearningQuestionnaire = (id: number) =>
  apiGet<EagleVisionResource<LearningQuestionnaire | (LearningQuestionnaire & { questions?: LearningQuestion[] })>>(`${learningBase}/questionnaires/${id}`)

export const getLearningQuestion = (questionnaireId: number, questionId: number) =>
  apiGet<EagleVisionResource<LearningQuestion>>(`${learningBase}/questionnaires/${questionnaireId}/questions/${questionId}`)

export const startLearningQuestionnaire = (id: number) =>
  apiPost<EagleVisionResource<unknown>>(`${learningBase}/questionnaires/${id}/start`)

export const submitLearningAnswer = (questionId: number, optionId: number) =>
  apiPost<EagleVisionResource<unknown>>(`${learningBase}/questions/${questionId}/answers/${optionId}`)

export const finishLearningQuestionnaire = (id: number) =>
  apiPost<FinishLearningQuestionnaireResponse>(`${learningBase}/questionnaires/${id}/finish`)

// ==================== Auth Status ====================

export const getEagleVisionStatus = async (): Promise<{ enabled: boolean; base_url?: string }> => {
  return apiGet('/auth/eagle-vision/status')
}

export const validateEagleVisionToken = async (): Promise<{ valid: boolean; message?: string; needs_auth?: boolean }> => {
  return apiPost('/auth/eagle-vision/validate')
}
